
import React, { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PostgrestError } from "@supabase/supabase-js";
import { Conta } from "@/types/database";
import { Plus, Edit, Trash2, Wallet, CreditCard, PiggyBank, Building } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useIsMobile } from "@/hooks/use-mobile";

import { EmptyState } from "../ui/EmptyState";

interface AccountManagerProps {
  contas: Conta[];
  loading: boolean;
  onCreate: (conta: Partial<Conta>) => Promise<{ error: PostgrestError | Error | null | unknown }>;
  onUpdate: (id: string, conta: Partial<Conta>) => Promise<{ error: PostgrestError | Error | null | unknown }>;
  onDelete: (id: string) => Promise<{ error: PostgrestError | Error | null | unknown }>;
  onRefresh: () => void;
}

export function AccountManager({
  contas,
  loading,
  onCreate,
  onUpdate,
  onDelete,
  onRefresh
}: AccountManagerProps) {
  const [editingConta, setEditingConta] = useState<Conta | null>(null);
  const [showContaDialog, setShowContaDialog] = useState(false);
  const [contaToDelete, setContaToDelete] = useState<Conta | null>(null);

  const isMobile = useIsMobile();

  // Form data para contas
  const [contaForm, setContaForm] = useState({
    nome: "",
    tipo: "carteira" as "carteira" | "conta_corrente" | "poupanca" | "cartao_credito",
    cor: "#3B82F6",
    saldo_inicial: "" as string | number,
    ativo: true,
    apelido: "",
  });

  const resetContaForm = () => {
    setContaForm({
      nome: "",
      tipo: "carteira",
      cor: "#3B82F6",
      saldo_inicial: "",
      ativo: true,
      apelido: "",
    });
    setEditingConta(null);
  };

  const handleSaveConta = async () => {
    if (!contaForm.nome.trim()) {
      toast.error("Nome da conta é obrigatório");
      return;
    }

    const saldoValue = parseFloat(contaForm.saldo_inicial.toString()) || 0;

    const payload: Partial<Conta> = {
      nome: contaForm.nome,
      tipo: contaForm.tipo,
      cor: contaForm.cor,
      saldo_inicial: saldoValue,
      saldo_atual: saldoValue, // Adicionando atualização do saldo_atual junto com saldo_inicial
      ativo: contaForm.ativo,
      apelido: contaForm.apelido || null,
    };

    try {
      if (editingConta) {
        const { error } = await onUpdate(editingConta.id, payload);
        if (error) throw error;
      } else {
        const { error } = await onCreate(payload);
        if (error) throw error;
      }

      setShowContaDialog(false);
      resetContaForm();
    } catch (error) {
      // Erro já tratado no hook
    }
  };

  const handleEditConta = (conta: Conta) => {
    setContaForm({
      nome: conta.nome,
      tipo: conta.tipo,
      cor: conta.cor,
      saldo_inicial: conta.saldo_atual || conta.saldo_inicial,
      ativo: conta.ativo,
      apelido: conta.apelido || "",
    });
    setEditingConta(conta);
    setShowContaDialog(true);
  };

  const handleAccountClick = (conta: Conta) => {
    // Allow click to edit on mobile and tablet (screens smaller than desktop)
    handleEditConta(conta);
  };

  const handleDeleteConta = (conta: Conta) => {
    setContaToDelete(conta);
  };

  const confirmDeleteConta = async () => {
    if (!contaToDelete) return;

    try {
      const { error } = await onDelete(contaToDelete.id);
      if (error) throw error;

      setContaToDelete(null);
    } catch (error) {
      // Erro já tratado no hook
      setContaToDelete(null);
    }
  };

  const getContaIcon = (tipo: string) => {
    switch (tipo) {
      case "carteira":
        return <Wallet className="h-4 w-4" />;
      case "conta_corrente":
        return <Building className="h-4 w-4" />;
      case "poupanca":
        return <PiggyBank className="h-4 w-4" />;
      case "cartao_credito":
        return <CreditCard className="h-4 w-4" />;
      default:
        return <Wallet className="h-4 w-4" />;
    }
  };

  const formatTipoNome = (tipo: string) => {
    const tipos = {
      carteira: "Carteira",
      conta_corrente: "Conta Corrente",
      poupanca: "Poupança",
      cartao_credito: "Cartão de Crédito",
    };
    return (tipos as Record<string, string>)[tipo] || tipo;
  };

  if (loading) {
    return <div className="p-6 text-center">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Dialog open={showContaDialog} onOpenChange={setShowContaDialog}>
                <DialogTrigger asChild>
                  <Button onClick={resetContaForm}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Conta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingConta ? "Editar Conta" : "Nova Conta"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="conta-nome">Nome</Label>
                      <Input
                        id="conta-nome"
                        value={contaForm.nome}
                        onChange={(e) => setContaForm(prev => ({ ...prev, nome: e.target.value }))}
                        placeholder="Nome da conta"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="conta-apelido">Apelido</Label>
                        <Input
                          id="conta-apelido"
                          value={contaForm.apelido}
                          onChange={(e) => setContaForm(prev => ({ ...prev, apelido: e.target.value }))}
                          placeholder="Ex.: Conta Salário"
                        />
                      </div>
                      <div>
                        <Label htmlFor="conta-tipo">Tipo</Label>
                        <Select
                          value={contaForm.tipo}
                          onValueChange={(value: "carteira" | "conta_corrente" | "poupanca" | "cartao_credito") =>
                            setContaForm(prev => ({ ...prev, tipo: value }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="carteira">Carteira</SelectItem>
                            <SelectItem value="conta_corrente">Conta Corrente</SelectItem>
                            <SelectItem value="poupanca">Poupança</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="conta-saldo">Saldo</Label>
                        <CurrencyInput
                          id="conta-saldo"
                          value={Number(contaForm.saldo_inicial)}
                          onChange={(value) => setContaForm(prev => ({ ...prev, saldo_inicial: value }))}
                          placeholder="0,00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="conta-cor">Cor</Label>
                        <Input
                          id="conta-cor"
                          type="color"
                          value={contaForm.cor}
                          onChange={(e) => setContaForm(prev => ({ ...prev, cor: e.target.value }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="conta-ativo">Ativo</Label>
                        <Switch
                          id="conta-ativo"
                          checked={contaForm.ativo}
                          onCheckedChange={(checked) => setContaForm(prev => ({ ...prev, ativo: checked }))}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowContaDialog(false);
                          resetContaForm();
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button onClick={handleSaveConta}>
                        {editingConta ? "Atualizar" : "Criar"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>


            </div>

          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {contas.length === 0 ? (
              <EmptyState
                icon={Wallet}
                title="Nenhuma conta"
                description="Você ainda não cadastrou nenhuma conta bancária ou carteira. Adicione uma para começar a gerenciar seu dinheiro!"
              />
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {contas.map((conta) => {
                  const saldoAtual = typeof conta.saldo_atual === "number" ? conta.saldo_atual : conta.saldo_inicial;
                  const isNegative = saldoAtual < 0;

                  return (
                    <div
                      key={conta.id}
                      className="p-4 border border-border rounded-lg hover:bg-accent/30 transition-all duration-300 hover:shadow-medium space-y-3 cursor-pointer"
                      onClick={() => handleAccountClick(conta)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: conta.cor }}
                          />
                          {getContaIcon(conta.tipo)}
                          <div>
                            <span className="font-medium">{conta.nome}</span>
                            {!conta.ativo && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Inativo
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Editar conta ${conta.nome}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditConta(conta);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Editar conta</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Excluir conta ${conta.nome}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteConta(conta);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Excluir conta</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Saldo Atual</span>
                          <span className={`text-lg font-bold tabular-nums tracking-tight ${isNegative ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                            }`}>
                            R$ {saldoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">Tipo</span>
                          <Badge variant={conta.ativo ? "default" : "secondary"} className="text-xs">
                            {formatTipoNome(conta.tipo)}
                          </Badge>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        {conta.ativo ? 'Conta ativa' : 'Conta inativa'} • {' '}
                        Saldo inicial: R$ {conta.saldo_inicial.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>



      <AlertDialog open={!!contaToDelete} onOpenChange={() => setContaToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a conta "{contaToDelete?.nome}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteConta} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
